import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { usePlayerProgression } from "./usePlayerProgression";
import { notifyWalletUpdated } from "./useWallet";
import { isQAOverrideUser } from "@/lib/qaOverride";
import { toast } from "sonner";

/**
 * XP per module. Persistent cash is NOT defined here — it is owned by the
 * server-side `module_reward_catalog` (standard $20 / substantial simulator
 * $30 / major capstone $40) and paid exactly once per learner per module by
 * `award_module_completion_reward`. XP may vary more freely than cash.
 */
const MODULE_XP: Record<string, number> = {
  account_setup: 50,
  budget_builder: 75,
  credit_management: 75,
  career_path: 80,
  vehicle_ownership: 100,
  risk_protection: 80,
  family_budget: 80,
  recovery_mode: 100,
  scam_defense: 100,
  resource_allocation: 100,
  market_dynamics: 100,
  industry_competition: 100,
  career_market: 100,
  macro_dashboard: 100,
  banking_system: 100,
  policy_tradeoff: 100,
  global_trade: 100,
  choice_architecture: 100,
  nation_builder: 120,
  strategy_arena: 120,
  crisis_timeline: 120,
  market_simulator: 150,
  tax_strategy: 100,
  wealth_strategy: 120,
  homeownership: 150,
  re_investing: 150,
  development: 150,
  entrepreneurship: 200,
  opportunity_finder: 120,
  offer_builder: 120,
  sales_conversation: 120,
  business_model_sim: 130,
  operations_engine: 130,
  founder_finance: 140,
  scale_strategy: 140,
  founder_decisions: 150,
  ownership_strategy: 150,
};

export function useModuleRewards() {
  const { user } = useAuth();
  const { awardXP } = usePlayerProgression();
  const claimedRef = useRef<Set<string>>(new Set());

  const completeModule = useCallback(async (moduleKey: string) => {
    if (!user || claimedRef.current.has(moduleKey)) return;
    // QA admins replay modules for testing — never duplicate XP/coins/mission progress.
    if (await isQAOverrideUser(user.id)) {
      claimedRef.current.add(moduleKey);
      toast.success("QA preview — rewards skipped", {
        description: `Module ${moduleKey} replay (no learner data written).`,
      });
      return;
    }
    claimedRef.current.add(moduleKey);

    const xp = MODULE_XP[moduleKey] ?? 50;
    const result = await awardXP(xp, `Module complete: ${moduleKey}`, "module", moduleKey);

    // Persistent cash is server-authoritative, catalogued and one-time.
    let coins = 0;
    const { data, error } = await supabase.rpc("award_module_completion_reward", {
      p_module_key: moduleKey,
    });
    if (error) {
      console.warn("[useModuleRewards] module reward rejected:", error.message);
    } else {
      coins = Number((data as { amount?: number } | null)?.amount ?? 0);
      if (coins > 0) notifyWalletUpdated();
    }

    toast.success(
      coins > 0 ? `Module Complete! +${xp} XP, +$${coins}` : `Module Complete! +${xp} XP`,
      { description: result?.leveledUp ? `🎉 Level up! ${result.newTitle}` : undefined },
    );

    // Try to increment mission progress
    void supabase.rpc("increment_mission_progress", { p_mission_key: "w_complete_module" });

    return result;
  }, [user, awardXP]);

  return { completeModule };
}
